/**
 * Filters (spec section 12).
 *
 * The same component renders as a bottom sheet on mobile and an inline sidebar
 * panel on desktop, so there is one filter implementation rather than two that
 * can drift apart.
 */

import { useEffect, useRef } from 'react';
import type { Facets, Filters } from '@/lib/nutrition/ranking';
import { countActiveFilters, emptyFilters } from '@/lib/nutrition/ranking';

interface Props {
  filters: Filters;
  facets: Facets;
  onChange: (filters: Filters) => void;
  /** Sheet mode on mobile; inline panel on desktop. */
  variant: 'sheet' | 'inline';
  open?: boolean;
  onClose?: () => void;
  resultCount: number;
}

export function FilterSheet({
  filters,
  facets,
  onChange,
  variant,
  open = true,
  onClose,
  resultCount,
}: Props): React.JSX.Element | null {
  const sheetRef = useRef<HTMLDivElement>(null);

  // Escape closes the sheet, and focus moves into it on open.
  useEffect(() => {
    if (variant !== 'sheet' || !open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    sheetRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [variant, open, onClose]);

  if (variant === 'sheet' && !open) return null;

  const toggleValue = (key: 'categories' | 'brands' | 'stores' | 'locations', value: string): void => {
    const current = filters[key];
    onChange({
      ...filters,
      [key]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
    });
  };

  const numberField = (
    key: 'minPrice' | 'maxPrice' | 'minProtein' | 'maxCalories' | 'minPackageSize' | 'maxPackageSize',
    label: string,
    placeholder: string,
  ): React.JSX.Element => (
    <>
      <label className="visually-hidden" htmlFor={`filter-${key}`}>
        {label}
      </label>
      <input
        id={`filter-${key}`}
        className="range-input"
        type="number"
        inputMode="decimal"
        min={0}
        step="any"
        placeholder={placeholder}
        value={filters[key] ?? ''}
        onChange={(e) =>
          onChange({
            ...filters,
            [key]: e.target.value === '' ? null : Number(e.target.value),
          })
        }
      />
    </>
  );

  const body = (
    <>
      <FacetGroup
        title="Category"
        options={facets.categories}
        selected={filters.categories}
        onToggle={(v) => toggleValue('categories', v)}
      />
      <FacetGroup
        title="Brand"
        options={facets.brands}
        selected={filters.brands}
        onToggle={(v) => toggleValue('brands', v)}
      />
      <FacetGroup
        title="Store"
        options={facets.stores}
        selected={filters.stores}
        onToggle={(v) => toggleValue('stores', v)}
      />
      <FacetGroup
        title="Location"
        options={facets.locations}
        selected={filters.locations}
        onToggle={(v) => toggleValue('locations', v)}
      />

      <div className="filter-group">
        <div className="filter-group-title">Price</div>
        <div className="range-row">
          {numberField('minPrice', 'Minimum price', 'Min')}
          <span aria-hidden="true">–</span>
          {numberField('maxPrice', 'Maximum price', 'Max')}
        </div>
      </div>

      <div className="filter-group">
        <div className="filter-group-title">Nutrition (per 100 g / 100 ml)</div>
        <div className="range-row">
          {numberField('minProtein', 'Minimum protein', 'Min protein g')}
          {numberField('maxCalories', 'Maximum calories', 'Max kcal')}
        </div>
        <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: '0.35rem 0 0' }}>
          Products without nutrition data are excluded by these filters rather than treated as zero.
        </p>
      </div>

      <div className="filter-group">
        <div className="filter-group-title">Package size (g / ml / pieces)</div>
        <div className="range-row">
          {numberField('minPackageSize', 'Minimum package size', 'Min')}
          <span aria-hidden="true">–</span>
          {numberField('maxPackageSize', 'Maximum package size', 'Max')}
        </div>
      </div>

      <div className="filter-group">
        <div className="filter-group-title">Availability</div>
        <div className="filter-options">
          {(['all', 'online', 'offline'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className="chip"
              aria-pressed={filters.availability === option}
              onClick={() => onChange({ ...filters, availability: option })}
            >
              {option === 'all' ? 'All' : option === 'online' ? 'Online' : 'In store'}
            </button>
          ))}
        </div>
      </div>

      <div className="filter-group">
        <label className="check-row">
          <input
            type="checkbox"
            checked={filters.requireNutrition}
            onChange={(e) => onChange({ ...filters, requireNutrition: e.target.checked })}
          />
          Only products with usable nutrition data
        </label>
      </div>
    </>
  );

  if (variant === 'inline') {
    return (
      <div className="panel">
        <div className="sheet-head">
          <h2 className="panel-title" style={{ marginBottom: 0 }}>
            Filters
          </h2>
          {countActiveFilters(filters) > 0 && (
            <button type="button" className="btn btn-sm" onClick={() => onChange(emptyFilters())}>
              Clear
            </button>
          )}
        </div>
        {body}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className="sheet-backdrop"
        aria-label="Close filters"
        onClick={onClose}
      />
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="filter-sheet-title"
        ref={sheetRef}
        tabIndex={-1}
      >
        <div className="sheet-grabber" aria-hidden="true" />
        <div className="sheet-head">
          <h2 id="filter-sheet-title">Filters</h2>
          <button type="button" className="btn btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
        {body}
        <div className="sheet-foot">
          <button type="button" className="btn" onClick={() => onChange(emptyFilters())}>
            Clear all
          </button>
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Show {resultCount} result{resultCount === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </>
  );
}

function FacetGroup({
  title,
  options,
  selected,
  onToggle,
}: {
  title: string;
  options: Array<{ value: string; count: number }>;
  selected: string[];
  onToggle: (value: string) => void;
}): React.JSX.Element | null {
  if (options.length === 0) return null;
  return (
    <div className="filter-group">
      <div className="filter-group-title">{title}</div>
      <div className="filter-options">
        {options.slice(0, 12).map((option) => (
          <button
            key={option.value}
            type="button"
            className="chip"
            aria-pressed={selected.includes(option.value)}
            onClick={() => onToggle(option.value)}
          >
            {option.value} <span style={{ opacity: 0.6 }}>{option.count}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
